import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useLocation, useNavigate, type Location } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { useAuthStore } from './authStore';
import { fetchCurrentUser, login } from '../api/endpoints/auth';
import { getRecaptchaToken } from './recaptcha';
import { RecaptchaCheckbox, isRecaptchaV2Enabled } from './RecaptchaCheckbox';
import { ApiError } from '../api/types/common';

const schema = z.object({
  username: z.string().min(1, 'Kullanıcı adı gerekli'),
  password: z.string().min(1, 'Şifre gerekli'),
});

type FormValues = z.infer<typeof schema>;

const REMEMBER_KEY = 'tfw:login:remembered-username';

function readRemembered(): string {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
}

const PROMO_CHIPS = [
  { icon: <FlightTakeoffOutlinedIcon fontSize="small" />, label: 'Uçuş Rezervasyonu' },
  { icon: <HotelOutlinedIcon fontSize="small" />, label: 'Otel Yönetimi' },
  { icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, label: 'Transfer & Araç' },
  { icon: <InsightsOutlinedIcon fontSize="small" />, label: 'Anlık Raporlar' },
];

const PROMO_STATS = [
  { num: '500+', label: 'Aktif Acente' },
  { num: '45 Yıl', label: 'Sektör Deneyimi' },
  { num: '7/24', label: 'Destek Hattı' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const setAwaitingOtp = useAuthStore((s) => s.setAwaitingOtp);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!readRemembered());
  const [recaptchaV2Token, setRecaptchaV2Token] = useState('');
  const [recaptchaResetKey, setRecaptchaResetKey] = useState(0);

  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset ?? false;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: readRemembered(), password: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      let recaptchaToken: string | undefined;
      if (isRecaptchaV2Enabled()) {
        if (!recaptchaV2Token) {
          setSubmitError('Lütfen "Ben robot değilim" doğrulamasını tamamlayın.');
          return;
        }
        recaptchaToken = recaptchaV2Token;
      } else {
        recaptchaToken = await getRecaptchaToken('login');
      }

      const session = await login(values.username, values.password, recaptchaToken);

      try {
        if (remember) window.localStorage.setItem(REMEMBER_KEY, values.username);
        else window.localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* localStorage kullanılamıyorsa "beni hatırla" sessizce atlanır */
      }

      if (session.requiresOtp) {
        setAwaitingOtp({
          sessionId: session.sessionId,
          maskedEmail: session.otpMaskedEmail,
          employeeId: session.employeeID,
        });
        navigate('/verify-otp', { replace: true });
        return;
      }
      setSession(
        { sessionId: session.sessionId, sessionToken: session.sessionToken },
        session.employeeID
      );
      const currentUser = await fetchCurrentUser();
      setCurrentUser(currentUser);
      const redirectTo = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Giriş yapılamadı. Lütfen tekrar deneyin.');
      // v2 token'ları tek kullanımlık — başarısız denemede kutuyu sıfırla.
      setRecaptchaV2Token('');
      setRecaptchaResetKey((k) => k + 1);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.paper' }}>
      {/* SOL — GİRİŞ FORMU */}
      <Box
        sx={{
          width: { xs: '100%', md: 460 },
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: { md: '1px solid' },
          borderColor: { md: 'divider' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto' }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              }}
            >
              <TravelExploreOutlinedIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.3px' }}>
              TravelFuture
            </Typography>
          </Stack>

          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.4px' }}>
            Hoş geldiniz
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 4 }}>
            TravelFuture hesabınıza giriş yapın.
          </Typography>

          <Stack
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            spacing={2}
            noValidate
          >
            {passwordReset && !submitError && (
              <Alert severity="success">
                Parolanız güncellendi. Yeni parolanızla giriş yapabilirsiniz.
              </Alert>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            <TextField
              label="Kullanıcı adı"
              autoComplete="username"
              autoFocus={!readRemembered()}
              fullWidth
              error={!!errors.username}
              helperText={errors.username?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineOutlinedIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                },
              }}
              {...register('username')}
            />
            <TextField
              label="Şifre"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              fullWidth
              error={!!errors.password}
              helperText={errors.password?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              {...register('password')}
            />

            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', mt: -0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Beni hatırla</Typography>}
              />
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                underline="hover"
              >
                Şifremi unuttum
              </Link>
            </Stack>

            {isRecaptchaV2Enabled() && (
              <RecaptchaCheckbox onChange={setRecaptchaV2Token} resetKey={recaptchaResetKey} />
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              startIcon={<LoginOutlinedIcon />}
            >
              {isSubmitting ? 'Giriş yapılıyor…' : 'Giriş yap'}
            </Button>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 5 }}
          >
            © {new Date().getFullYear()} TravelFuture — Kurumsal Seyahat Paneli
          </Typography>
        </Box>
      </Box>

      {/* SAĞ — TANITIM */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          px: 8,
          backgroundImage: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${t.palette.primary.light} 130%)`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 78% 18%, rgba(255,255,255,0.08) 0%, transparent 45%), radial-gradient(circle at 12% 88%, rgba(0,0,0,0.18) 0%, transparent 42%)',
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 460 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}
          >
            Turizm Teknolojisi
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.2, mt: 1.5 }}
          >
            Operasyonunuzu tek panelden yönetin
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, mt: 2, maxWidth: 400 }}
          >
            Uçuş, otel, transfer ve araç kiralama süreçlerinizi tek platformdan yürütün;
            gerçek zamanlı takip ve anlık raporlarla kontrolü elden bırakmayın.
          </Typography>

          <Stack direction="row" useFlexGap sx={{ gap: 1, mt: 4, flexWrap: 'wrap' }}>
            {PROMO_CHIPS.map((c) => (
              <Stack
                key={c.label}
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 5,
                  fontSize: 13,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {c.icon}
                <span>{c.label}</span>
              </Stack>
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={4}
            sx={{ mt: 5, pt: 3.5, borderTop: '1px solid rgba(255,255,255,0.16)' }}
          >
            {PROMO_STATS.map((s) => (
              <Box key={s.label}>
                <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{s.num}</Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    mt: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'rgba(255,255,255,0.55)',
                  }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
